import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import { setlistSchemas } from '@/api/setlists/setlist.schema';
import { Button } from '@/components/ui/button';
import { KeyBadge } from '@/components/ui/cifras/key-badge';
import { InfiniteScrollSentinel, ListSearch } from '@/components/ui/cifras/list-toolbar';
import { Form, FormContent } from '@/components/ui/form';
import { FormAsyncSelect } from '@/components/ui/form-fields/form-async-select';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { isAdminTierRole } from '@/lib/permissions';
import { SONG_KEYS } from '@/lib/song';
import { cn } from '@/lib/utils';
import { useIsAuthenticated } from '@/services/auth';
import {
  type SetlistDetail,
  useCreateSetlist,
  useReplaceSetlistSongs,
  useUpdateSetlist,
} from '@/services/setlists';
import { useInfiniteSongs } from '@/services/songs';
import { createUserOptionsQuery, type UserOption } from '@/services/users';
import type { ValidationError } from '@/types/api/validation-error';

type Schema = import('zod').input<typeof setlistSchemas.create>;

type Entry = {
  songId: string;
  title: string;
  artist: string | null;
  originalKey: string;
  /** Tom no repertório; `null` = tom original. */
  key: string | null;
};

const entriesOf = (setlist?: SetlistDetail): Entry[] =>
  (setlist?.items ?? []).map((item) => ({
    songId: item.song.id,
    title: item.song.title,
    artist: item.artist?.name ?? null,
    originalKey: item.song.originalKey,
    key: item.key,
  }));

const userOptions = createUserOptionsQuery();

const SortableEntry: React.FC<{
  entry: Entry;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
  onKey: (songId: string, key: string | null) => void;
  onRemove: (songId: string) => void;
}> = ({ entry, index, total, onMove, onKey, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: entry.songId });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-3 rounded-[12px] border border-border bg-raised px-3 py-2.5',
        isDragging && 'relative z-10 border-accent-line shadow-[var(--shadow-float)]',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-faint hover:text-white active:cursor-grabbing"
        aria-label={`Arrastar ${entry.title}`}
        {...attributes}
        {...listeners}
      >
        <Icon name="grip-vertical" className="size-4" />
      </button>

      <span className="w-5 text-center font-bold text-accent-ink">{index + 1}</span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-[14px] text-white">{entry.title}</p>
        <p className="truncate text-[12px] text-muted-foreground">
          {entry.artist ?? 'Sem artista'} · original {entry.originalKey}
        </p>
      </div>

      <select
        aria-label={`Tom de ${entry.title}`}
        value={entry.key ?? ''}
        onChange={(event) => onKey(entry.songId, event.target.value || null)}
        className="h-8 rounded-[8px] border border-border bg-field px-2 font-bold text-[12px] text-accent-soft outline-none focus:border-accent-line"
      >
        <option value="">Original ({entry.originalKey})</option>
        {SONG_KEYS.map((key) => (
          <option key={key} value={key}>
            {key}
          </option>
        ))}
      </select>

      <div className="flex flex-col">
        <button
          type="button"
          aria-label="Subir"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          className="text-faint hover:text-white disabled:opacity-30"
        >
          <Icon name="chevron-up" className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Descer"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          className="text-faint hover:text-white disabled:opacity-30"
        >
          <Icon name="chevron-down" className="size-4" />
        </button>
      </div>

      <button
        type="button"
        aria-label={`Remover ${entry.title}`}
        onClick={() => onRemove(entry.songId)}
        className="flex size-8 items-center justify-center rounded-[8px] border border-destructive/40 text-destructive transition-colors hover:bg-destructive/10"
      >
        <Icon name="x" className="size-4" />
      </button>
    </li>
  );
};

/**
 * Editor de repertório (criar e editar): dados + busca de músicas + lista ordenável.
 * Só músicas **ativas** aparecem na busca; inativa que já está no repertório continua nele.
 */
export const SetlistEditor: React.FC<{ setlist?: SetlistDetail }> = ({ setlist }) => {
  const router = useRouter();
  const { user } = useIsAuthenticated();
  const isAdmin = isAdminTierRole(user?.role);

  const { mutateAsync: create } = useCreateSetlist();
  const { mutateAsync: update } = useUpdateSetlist();
  const { mutateAsync: replaceSongs } = useReplaceSetlistSongs();
  const [isSaving, setIsSaving] = useState(false);

  const [entries, setEntries] = useState<Entry[]>(() => entriesOf(setlist));
  const [search, setSearch] = useState('');

  const {
    data: found,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSongs({
    search: search || undefined,
    isActive: 'true',
    orderBy: 'title',
    order: 'asc',
    perPage: 20,
  });
  const songs = found?.pages.flatMap((page) => page.data) ?? [];

  const form = useForm<Schema>({
    resolver: zodResolver(setlistSchemas.create) as any,
    shouldUseNativeValidation: false,
    defaultValues: {
      name: setlist?.name ?? '',
      userId: setlist?.userId,
      isActive: setlist?.isActive ?? true,
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const move = (from: number, to: number) =>
    setEntries((current) => arrayMove(current, from, to));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setEntries((current) => {
      const from = current.findIndex((entry) => entry.songId === active.id);
      const to = current.findIndex((entry) => entry.songId === over.id);
      return arrayMove(current, from, to);
    });
  };

  const handleSubmit = async (values: Schema) => {
    setIsSaving(true);

    try {
      const saved = setlist
        ? await update({ id: setlist.id, data: values as any })
        : await create(values as any);
      const id = setlist?.id ?? (saved as { id: string }).id;

      await replaceSongs({
        id,
        items: entries.map(({ songId, key }) => ({ songId, key: key as any })),
      });

      router.push('/setlists');
    } catch (error: any) {
      if (error?.status === 422) {
        const { errors } = error.value as unknown as ValidationError<Schema>;
        for (const [key, message] of Object.entries(errors ?? {})) {
          form.setError(key as keyof Schema, { message: message as string });
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // novo repertório: o responsável é sempre quem está logado (admin pode trocar)
    if (!setlist && user?.id) form.setValue('userId', user.id);
  }, [setlist, user?.id, form]);

  return (
    <div className="flex flex-col gap-5">
      <Form
        id="form"
        title="Dados do repertório"
        description={
          setlist
            ? `Responsável: ${setlist.user?.name ?? '—'}`
            : 'Você será o responsável por este repertório.'
        }
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <FormInput name="name" control={form.control} label="Nome" required />
          {isAdmin && (
            <FormAsyncSelect<Schema, UserOption>
              name="userId"
              control={form.control as Control<Schema>}
              label="Responsável"
              useQuery={userOptions}
              getOptionValue={(option) => option.id}
              getDisplayValue={(option) => option.name}
              clearable={false}
            />
          )}
        </FormContent>
      </Form>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="flex flex-col gap-4 rounded-[16px] border border-border bg-card p-[26px]">
          <h2 className="font-bold font-display text-[16px] text-white uppercase tracking-tight">
            Buscar músicas
          </h2>
          <ListSearch
            placeholder="Buscar por música ou artista..."
            onSearch={setSearch}
          />

          <ul className="flex max-h-[560px] flex-col gap-2 overflow-y-auto pr-1">
            {songs.map((song) => {
              const included = entries.some((entry) => entry.songId === song.id);
              return (
                <li
                  key={song.id}
                  className="flex items-center gap-3 rounded-[12px] border border-border bg-raised px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-[14px] text-white">
                      {song.title}
                    </p>
                    <p className="truncate text-[12px] text-muted-foreground">
                      {song.artist?.name ?? 'Sem artista'} · {song.originalKey}
                    </p>
                  </div>
                  {included ? (
                    <span className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-success/10 px-3 font-semibold text-[12px] text-success">
                      <Icon name="check" className="size-3.5" />
                      No repertório
                    </span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEntries((current) => [
                          ...current,
                          {
                            songId: song.id,
                            title: song.title,
                            artist: song.artist?.name ?? null,
                            originalKey: song.originalKey,
                            key: null,
                          },
                        ])
                      }
                    >
                      <Icon name="plus" />
                      Adicionar
                    </Button>
                  )}
                </li>
              );
            })}
            {found && songs.length === 0 && (
              <li className="py-8 text-center text-[13px] text-muted-foreground">
                Nenhuma música ativa encontrada.
              </li>
            )}
            <li>
              <InfiniteScrollSentinel
                hasNextPage={Boolean(hasNextPage)}
                isFetching={isFetchingNextPage}
                onLoadMore={fetchNextPage}
              />
            </li>
          </ul>
        </section>

        <section className="flex flex-col gap-4 rounded-[16px] border border-border bg-card p-[26px]">
          <div className="flex items-center gap-2.5">
            <h2 className="font-bold font-display text-[16px] text-white uppercase tracking-tight">
              Repertório
            </h2>
            <KeyBadge value={String(entries.length)} />
          </div>

          {entries.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-muted-foreground">
              Adicione músicas pela busca ao lado.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={entries.map((entry) => entry.songId)}
                strategy={verticalListSortingStrategy}
              >
                <ol className="flex flex-col gap-2">
                  {entries.map((entry, index) => (
                    <SortableEntry
                      key={entry.songId}
                      entry={entry}
                      index={index}
                      total={entries.length}
                      onMove={move}
                      onKey={(songId, key) =>
                        setEntries((current) =>
                          current.map((item) =>
                            item.songId === songId ? { ...item, key } : item,
                          ),
                        )
                      }
                      onRemove={(songId) =>
                        setEntries((current) =>
                          current.filter((item) => item.songId !== songId),
                        )
                      }
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          )}
        </section>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={isSaving}
          onClick={() => router.back()}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          form="form"
          variant="success"
          disabled={isSaving}
          className="flex items-center gap-2"
        >
          <Icon name="save" />
          Salvar repertório
        </Button>
      </div>
    </div>
  );
};
